import {useState, useEffect, useRef} from 'react';
import styles from '../assets/styles/scanner.module.css';
import QRScanner from '../components/QRScanner';
import Backarrow from '../components/Backarrow';
import { useAuth } from '../lib/firebase';
import { getCurrentData, getCompressedQRData, canGenerateQRCodePayload, decodeDataMapPayload, basicUploadChecks, uploadData, loadDataMap, deleteDataMap, getAllSavedMatchNumbers, formatData, saveDataMap, isValidData } from '../lib/data';
import Toast from '../lib/toast';
import { playErrorSound,playUploadSound } from '../lib/audio';
import { QRCodeSVG } from 'qrcode.react';
import { useDeviceInfo } from '../lib/mobile';
import {gamemodes} from '../configs/generalConfig';
import { enqueueRecord, queueEditedRecord, markFirebaseUploaded } from '../lib/queue';
import { EDITED_AT_KEY, SUBMISSION_ID_KEY } from '../lib/submissionId';
import { primeUsbPermission, writeQueueToUsb } from '../lib/usb';
import confetti from 'canvas-confetti';

function Scanner({currentTab, setCurrentTab,eventPath,eventList,sfxEnabled,funnySounds,onlineStatus, settingsYear, gamemode}) {
    const {authentication,user,setAuthentication} = useAuth();
    const [scanResult, setScanResult] = useState("");
    const [matchInput, setMatchInput] = useState("");
    const {device} = useDeviceInfo();
    const [fullscreenQR, setFullscreenQR] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const [maxSize,setMaxSize] = useState(0);
    const [useCompressedPayload, setUseCompressedPayload] = useState(false);
    const [toggleTimeStamps, setToggleTimeStamps] = useState(false);
    // The mode this screen searches, uploads and exports in - starts at Settings' mode; changing it
    // re-files a loaded match (e.g. a Quals match that was really Practice) when it's uploaded again.
    const [uploadMode, setUploadMode] = useState(gamemode);
    useEffect(() => { setUploadMode(gamemode); }, [gamemode]);

    // Every match as it was loaded into the text box - searched, scanned or imported - by
    // its full content. Upload and Export compare against these to tell an edit from an
    // untouched copy, whatever the box shows (compressed or not). An edit is a correction:
    // it may overwrite Firebase and replaces the queued copy; an untouched copy never
    // overwrites (lib/submissionId.js, EDITED_AT_KEY).
    const loadedMatches = useRef(new Set());

    function rememberLoaded(text) {
        String(text || "").split("\n").forEach((line) => {
            const parsed = line.trim() ? decodeDataMapPayload(line) : null;
            if (parsed) loadedMatches.current.add(getCurrentData(parsed));
        });
    }

    /** The match marked as edited (stamped with when), or null if it is as loaded. */
    function editedCopy(map, now) {
        if (!map.get(EDITED_AT_KEY) && loadedMatches.current.has(getCurrentData(map))) return null;
        const copy = new Map(map);
        copy.set(EDITED_AT_KEY, map.get(EDITED_AT_KEY) || now);
        return copy;
    }

    /** Keep the edited version as this laptop's saved copy too. */
    function saveEditedCopy(map) {
        const num = map.get("match_number");
        if (num) saveDataMap(num, map, uploadMode);
    }

    function convertPayloadText(text, toCompressed) {
        const lines = text.split("\n");
        const converted = lines.map((line) => {
            if (line.trim() === "") return line;
            const parsed = decodeDataMapPayload(line);
            if (!parsed) return line;
            return toCompressed ? getCompressedQRData(parsed) : getCurrentData(parsed);
        });
        return converted.join("\n");
    }

    function setPayloadMode(toCompressed) {
        if (toCompressed === useCompressedPayload) return;

        if (toCompressed) {
            const convertedCompressed = inputValue.trim() === "" ? inputValue : convertPayloadText(inputValue, true);
            if (!canGenerateQRCodePayload(convertedCompressed)) {
                // eslint-disable-next-line no-restricted-globals
                alert("Unable to form a scannable compressed QR code. Staying on uncompressed mode.");
                setUseCompressedPayload(false);
                return;
            }

            setUseCompressedPayload(true);
            if (inputValue.trim() !== "") {
                setInputValue(convertedCompressed);
            }
            return;
        }

        setUseCompressedPayload(toCompressed);
        if (inputValue.trim() === "") return;
        setInputValue(convertPayloadText(inputValue, toCompressed));
    }

    function togglePayloadMode() {
        setPayloadMode(!useCompressedPayload);
    }

    function handleScannedPayload(scannedText) {
        const parsed = decodeDataMapPayload(scannedText);
        if (parsed) {
            // Always show scanned payloads uncompressed for readability/editability.
            setUseCompressedPayload(false);
            rememberLoaded(getCurrentData(parsed));
            setInputValue(getCurrentData(parsed));
            return;
        }

        setUseCompressedPayload(false);
        rememberLoaded(scannedText);
        setInputValue(scannedText);
    }


    function searchMatch(matchNum){
        let searchedDataMap = loadDataMap(parseInt(matchNum), uploadMode);

        if(toggleTimeStamps){
            searchedDataMap = loadDataMap(parseInt(matchNum),4) // 4 = timestamp data here
        }
        console.log("searched map: ", searchedDataMap);
        //console.log(typeof(searchedDataMap))
        if(searchedDataMap == null){
            Toast.error("" + gamemodes[uploadMode] + " Match not Found");
            return false;
        }
        setUseCompressedPayload(false);
        rememberLoaded(getCurrentData(searchedDataMap));
        setInputValue(getCurrentData(searchedDataMap));
        Toast.success("Match Found")
        return true;
    }

    function clearAllData(){
        // eslint-disable-next-line no-restricted-globals
        if(confirm("This will delete all saved data. It is permanent. Proceed?")){
            // eslint-disable-next-line no-restricted-globals
            if(confirm("R u sure??")){
                // eslint-disable-next-line no-restricted-globals
                if(confirm("Final check. The data will be gone forever.")){
                    getAllSavedMatchNumbers(uploadMode).forEach(num => {
                        deleteDataMap(num, uploadMode);
                    });
                    Toast.success("Data Cleared")
                }
                else{
                    return;
                }
            }
            else{
                return;
            }
        }
        else{
            return
        }
    }

    /**
     * Handles changes to the scanned results textbox
     * @param {*} e 
     */
    function handleChange(e) {
        e.preventDefault();
        setInputValue(e.target.value);
    }


    /**
     * Uploads all data in the textbox. Also handles mutiple uploads at once.
     * @returns 
     */
    /**
     * The Settings USB folder, like Submit: rewrite this laptop's queue file there. `usbReady` is
     * primeUsbPermission(), started at the very start of the tap (the browser only lets a page ask
     * for folder access during one). Quiet when no folder is set or the drive isn't plugged in.
     */
    async function writeUsbCopy(usbReady) {
        if (!(await usbReady)) return;
        const result = await writeQueueToUsb(eventPath);
        if (result.ok) Toast.success("Also saved to the USB folder");
    }

    async function uploadDataBatch(){
        if(scanResult == ""){
            Toast.error("Empty Push: Upload not Registered",funnySounds,sfxEnabled);
            return false;
        }
        const usbReady = primeUsbPermission();
        const res = basicUploadChecks(user,authentication,scanResult,eventPath,eventList,onlineStatus, settingsYear);
        if(res === false){
            playErrorSound(funnySounds,sfxEnabled);
            return;
        }
        const dataList = scanResult.split("\n");
        const now = Date.now();

        let succesfulUpload = false;
        const badData = [];
        for (let i = 0; i < dataList.length; i++) {
            if (dataList[i].trim() == "") continue;

            const parsed = decodeDataMapPayload(dataList[i]);
            if (!parsed) {
                Toast.error("Could not read a line - left in the box");
                badData.push(dataList[i]);
                continue;
            }
            const edited = editedCopy(parsed, now);

            // Awaited: the upload's answer decides whether the line leaves the box. An
            // untouched copy already in Firebase is left alone ('exists') - that is done too.
            // uploadData validates first: an invalid match is toasted and nothing is saved.
            const luffy = await uploadData(user,authentication,edited || parsed,eventPath,eventList,onlineStatus,settingsYear, uploadMode, { onlyIfAbsent: !edited })

            if (luffy === true || luffy === 'exists') {
                succesfulUpload = true;
                if (edited) saveEditedCopy(edited);
                // Into the Relay queue too (already uploaded, so Relay won't send it again), so the
                // USB file and the Pi have it.
                const formatted = formatData(edited || parsed);
                if (formatted) {
                    if (edited) queueEditedRecord(formatted, uploadMode, eventPath);
                    else enqueueRecord(formatted, uploadMode, eventPath);
                    if (formatted[SUBMISSION_ID_KEY]) markFirebaseUploaded([formatted[SUBMISSION_ID_KEY]], eventPath);
                }
            }
            else{
                badData.push(dataList[i]);
            }
        }

        if(succesfulUpload == true) {
            playUploadSound(funnySounds,sfxEnabled);
            void writeUsbCopy(usbReady);
            let newText = ""
            for(let i = 0; i < badData.length;i++){
                newText += badData[i] + "\n" + "\n";
            }
            setInputValue(newText);
        }
        else playErrorSound(funnySounds,sfxEnabled);
        return;
    }

    useEffect(() => {
        const handler = setTimeout(() => {
            setScanResult(inputValue);
        }, 300); // 0.3s after user stops typing/scanning

        return () => clearTimeout(handler);
    }, [inputValue]);

    function handleExport() {
        const lines = scanResult.split('\n').filter(line => line.trim() !== '');
        if (lines.length === 0) {
            Toast.error('Nothing to export', funnySounds, sfxEnabled);
            return;
        }
        const usbReady = primeUsbPermission();
        let count = 0;
        let alreadyQueued = 0;
        let updated = 0;
        let invalid = 0;
        const now = Date.now();
        for (const line of lines) {
            try {
                const dataMapResult = decodeDataMapPayload(line);
                if (!dataMapResult) continue;
                const edited = editedCopy(dataMapResult, now);
                const formatted = formatData(edited || dataMapResult);
                if (!formatted) continue;
                // Invalid: toasted by isValidData, and not saved, queued or written to USB.
                if (!isValidData(formatted, settingsYear)) {
                    invalid++;
                    continue;
                }
                if (edited) {
                    // An edit replaces the queued copy (and goes to the Pi as a new version).
                    saveEditedCopy(edited);
                    const result = queueEditedRecord(formatted, uploadMode, eventPath);
                    if (result === 'updated') updated++;
                    else if (result === 'added') count++;
                    continue;
                }
                // A QR carries its match's id; scanning one already in the queue adds nothing.
                if (enqueueRecord(formatted, uploadMode, eventPath)) count++;
                else alreadyQueued++;
            } catch { /* skip unparseable lines */ }
        }
        // Already-queued matches are rewritten too: the drive may not have had them yet.
        if (updated > 0 || count > 0 || alreadyQueued > 0) void writeUsbCopy(usbReady);
        if (updated > 0) {
            Toast.success(`${updated} edited record${updated !== 1 ? 's' : ''} updated in the export queue`);
            if (count === 0 && alreadyQueued === 0) return;
        }
        const already = alreadyQueued > 0 ? ` (${alreadyQueued} already queued)` : '';
        if (count > 0) {
            Toast.success(`${count} record${count !== 1 ? 's' : ''} added to export queue${already}`, funnySounds, sfxEnabled);
            confetti({ particleCount: 300, spread: 600, origin: { y: 0.6 } });
        } else if (alreadyQueued > 0) {
            Toast.info(`${alreadyQueued} record${alreadyQueued !== 1 ? 's' : ''} already in the export queue`);
        } else if (invalid > 0) {
            playErrorSound(funnySounds, sfxEnabled); // each invalid match already has its toast
        } else {
            Toast.error('Could not parse any records', funnySounds, sfxEnabled);
        }
    }

    /**
     * For importing json datafiles
     * @param {*} e 
     * @returns 
     */
    async function handleMobileImport(e) {
        try{
            const file = e.target.files[0];
            if (!file) return;

            if (!file.name.endsWith(".json")) {
                Toast.error("Please select a .json file");
                return;
            }

            const text = await file.text();
            rememberLoaded(text);
            setInputValue(convertPayloadText(text, useCompressedPayload));
            Toast.info(`File "${file.name}" imported successfully`);
        } catch (err){
            console.error(err);
            Toast.error("File import failed");
        }
        
    }

    function toggleFullscreenQR(){
        setFullscreenQR(!fullscreenQR);
        let size = device == "desktop" ? window.visualViewport.height : window.visualViewport.width
        setMaxSize(size - 50)
        
    }

    const [filtered, setFiltered] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [highlighted, setHighlighted] = useState(-1);
    const listRef = useRef(null);
    const matchRef = useRef(null);

    function handleMatchChange(e) {
        const numberedMatchList = getAllSavedMatchNumbers(uploadMode);
        const matchList = numberedMatchList.map(item => String(item));
        const val = e.target.value;
        setMatchInput(val);
        if (val.length > 0) {
            setFiltered(matchList.filter(ev => ev.toLowerCase().includes(val.toLowerCase())));
            setShowSuggestions(true);
            setHighlighted(-1);
        } else {
            setShowSuggestions(false);
        }
    };

    /**
     * Handles enter keypresses
     */
    useEffect(() => {
        if (!matchRef.current) return;
        const handleEnter = (e) => {
            if (document.activeElement === matchRef.current) {
                if(e.key === "Escape"){
                    e.preventDefault();
                    setShowSuggestions(false);
                    matchRef.current.blur()
                }
                if(highlighted === -1){
                    if(e.key === "Enter"){
                        const result = searchMatch(matchInput);
                        if(result) {
                            setShowSuggestions(false);
                            matchRef.current.blur()
                        };
                    }
                    else if(e.key === "ArrowUp" || e.key === "ArrowDown"){
                        e.preventDefault();
                    }
                }                
            }
        };

        matchRef.current.addEventListener("keydown", handleEnter);
        return () => matchRef.current.removeEventListener("keydown", handleEnter);
    }, [highlighted, matchInput]);

    function handleKeyDown(e){
        if (!showSuggestions) return;
        
        if (e.key === "ArrowDown") {
            e.preventDefault();
            const next = Math.min(highlighted + 1, filtered.length - 1);
            setHighlighted(next);
            if (next >= 0) scrollIntoView(next);
        } 
        else if (e.key === "ArrowUp") {
            e.preventDefault();
            const prev = Math.max(highlighted - 1, -1);
            setHighlighted(prev);
            if (prev >= 0) scrollIntoView(prev);
            else  matchRef.current.focus();
        } 
        else if (e.key === "Enter" && highlighted >= 0) {
            e.preventDefault();
            handleSelect(filtered[highlighted]);
        }
    };
    /**
     * For when the autocomp li's are clicked
     * @param {} val 
     */
    const handleSelect = (val) => {
        setMatchInput(val);
        setShowSuggestions(false);
        matchRef.current.focus();
        setHighlighted(-1); //add feautre to NOT handle select when trying to scroll in the list on mobile
    };
    const scrollIntoView = (index) => {
        const list = listRef.current;
        if (list && list.children[index]) {
        list.children[index].scrollIntoView({ block: "nearest", behavior: "smooth" });
        }
    };


    if(currentTab === "scanner"){
        const canShowQr = canGenerateQRCodePayload(scanResult);
        const compressedCandidate = inputValue.trim() === "" ? "" : convertPayloadText(inputValue, true);
        const canUseCompressedQr = compressedCandidate === "" ? true : canGenerateQRCodePayload(compressedCandidate);
        const qrUnavailableHint = useCompressedPayload
            ? "Unable to form a scannable QR code. Use match data."
            : (canUseCompressedQr
                ? "Unable to form a scannable QR code. Use compressed."
                : "Unable to form a scannable QR code. Use match data.");
        return (
            <div className={device === "desktop" ? 'subpage' : styles.mobilePage}>
                <Backarrow setCurrentTab={setCurrentTab}/>
                <div className={styles.matchInputContainer}>
                    <label className={styles.switch}>
                        <input type="checkbox" checked={toggleTimeStamps} onChange={() => {setToggleTimeStamps(!toggleTimeStamps)}} />
                        <span className={styles.slider}></span>
                    </label>
                    <span>{toggleTimeStamps ? "Timekeeper Upload" : "Normal Upload"}</span>
                    <div className={styles.searchRow}>
                        <input
                            type='number'
                            ref={matchRef}
                            inputMode='numeric'
                            placeholder='Match Number'
                            value={matchInput}
                            className={`${styles.searchField} textInput`}
                            onChange={(e) => {handleMatchChange(e);}}
                            onKeyDown={(e) => handleKeyDown(e)}
                            onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                        />
                        <button className={`${styles.searchBtn}`} onClick={() => {
                                searchMatch(matchInput);
                            }}>Search Match
                        </button>
                    </div>
                    
                    {showSuggestions && filtered.length > 0 && (
                        <ul ref={listRef} className={styles.autocomplist}>
                            {filtered.map((ev, i) => (
                                <li 
                                onClick={() => handleSelect(ev)}
                                onTouchStart={() => setHighlighted(i)}
                                onTouchEnd={() => handleSelect(ev)}
                                key={i} 
                                style={{
                                    backgroundColor: i === highlighted ? "var(--accentColor)" : "",
                                    color: i === highlighted ? "var(--mainColor)" : "",
                                    transition: "background-color 0.2s ease-in-out",
                                }}
                                >
                                {ev}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className={`${styles.camBtn}`}>
                    <QRScanner setScanResult={handleScannedPayload}/>
                </div>
                <div className={styles.buttonBox}>
                    <div className={`${styles.gamemodeDisplay}`}>
                        <label htmlFor="scannerUploadMode"><strong>Upload Mode: </strong></label>
                        <select
                            id="scannerUploadMode"
                            className={styles.modeSelect}
                            value={uploadMode}
                            onChange={(e) => setUploadMode(Number(e.target.value))}
                        >
                            {gamemodes.slice(0, 4).map((name, i) => (<option key={name} value={i}>{name}</option>))}
                        </select>
                    </div>
                    <input
                        type="file"
                        accept=".json"
                        id="fileInput"
                        style={{ display: "none" }}
                        onChange={handleMobileImport}
                    />
                    <button onClick={() => document.getElementById("fileInput").click()} className={`${styles.inportBtn} uiButton`}>
                        Import JSON
                    </button>
                    <button className={`${styles.clearBtn} uiButton`} onClick={() => {
                            clearAllData();
                        }}>Clear Data
                    </button>
                </div>
                <div className={styles.payloadModeContainer}>
                    <label className={styles.payloadModeLabel}>
                        <input
                            type="checkbox"
                            checked={useCompressedPayload}
                            onChange={togglePayloadMode}
                        />
                        <span>Compressed Mode</span>
                    </label>
                </div>
                <div
                    className={fullscreenQR === false ? styles.qrContainer : styles.fullQrContainer}
                    onClick={() => {
                        if (canShowQr) toggleFullscreenQR();
                    }}
                    style={{ cursor: canShowQr ? 'pointer' : 'default' }}
                >
                    <div className={styles.qrInner}>
                        {!canShowQr ? (
                            <div>
                                <p className={styles.qrUnavailable}>{qrUnavailableHint}</p>
                            </div>
                        ) : (
                            <QRCodeSVG
                            value={scanResult}
                            size={fullscreenQR === false ? (device === "desktop" ? 350 : 156) : (maxSize)}
                            level="L"
                            />
                        )}
                    </div>
                </div>
                    
                
                <textarea onChange={(e) => handleChange(e)} value={inputValue} className={styles.scanResult}></textarea>
                <div className={styles.buttonBoxBottom}>
                    <button
                    className={`${styles.uploadBtn} uiButton`}
                    onClick={e => {
                        uploadDataBatch()
                        }}> 
                        Upload
                    </button>
                    <button
                        className={`${styles.shareBtn} uiButton`}
                        onClick={() => handleExport()}>
                            Export
                    </button>
                </div>
                
            </div>
        )
    } 
}

export default Scanner;